import { Challenge } from '../types';

export const challenges: Challenge[] = [
  {
    id: '1',
    title: 'Rule of Thirds',
    description: 'Capture an image where the main subject is positioned at one of the intersections of the rule of thirds grid. This fundamental composition technique helps create more balanced and interesting photographs that naturally draw the viewer\'s eye to the important elements of your scene.',
    category: 'Composition',
    difficulty: 'Beginner',
    technicalGuidance: [
      'Enable grid overlay in your camera settings',
      'Place key elements along the lines or at intersections',
      'Experiment with different subject placements',
      'Consider leaving negative space in the frame'
    ],
    creativeApproach: [
      'Try breaking the rule intentionally for dramatic effect',
      'Use the grid to balance multiple subjects',
      'Combine with leading lines for added impact',
      'Practice with both landscape and portrait orientations'
    ],
    inspiration: [
      {
        url: 'https://images.pexels.com/photos/1252983/pexels-photo-1252983.jpeg',
        description: 'A lighthouse positioned at the intersection of thirds, with the horizon line along the lower third'
      },
      {
        url: 'https://images.pexels.com/photos/1366919/pexels-photo-1366919.jpeg',
        description: 'Portrait using rule of thirds with subject\'s eyes positioned along the upper third line'
      }
    ],
    featuredIn: [
      {
        title: 'Understanding Composition by Bryan Peterson',
        source: 'https://www.amazon.com/Learning-See-Creatively-Composition-Photography/dp/1607748274'
      }
    ]
  },
  {
    id: '2',
    title: 'Golden Hour',
    description: 'Shoot during the first hour after sunrise or the last hour before sunset to capture the warm, soft light. This magical time of day provides perfect conditions for stunning portraits, landscapes, and architectural photography.',
    category: 'Lighting',
    difficulty: 'Beginner',
    technicalGuidance: [
      'Use a wider aperture to capture the soft light',
      'Keep ISO low to maintain image quality',
      'Consider using a lens hood to prevent flare',
      'Monitor your white balance settings'
    ],
    creativeApproach: [
      'Experiment with backlighting and silhouettes',
      'Look for opportunities to capture long shadows',
      'Use the warm tones to enhance mood',
      'Try both direct and diffused lighting angles'
    ],
    inspiration: [
      {
        url: 'https://images.pexels.com/photos/1237119/pexels-photo-1237119.jpeg',
        description: 'Landscape photo capturing the warm golden hour light with long shadows'
      },
      {
        url: 'https://images.pexels.com/photos/1757363/pexels-photo-1757363.jpeg',
        description: 'Portrait utilizing golden hour backlighting for a warm, dreamy effect'
      }
    ],
    featuredIn: [
      {
        title: 'Magic Hour Photography Guide',
        source: 'https://digital-photography-school.com/golden-hour-photography/'
      }
    ]
  },
  {
    id: '3',
    title: 'Long Exposure',
    description: 'Use a slow shutter speed (1/15 or slower) to capture motion blur in a moving subject. This technique can create dramatic effects with water, clouds, traffic, or any scene with movement.',
    category: 'Technical',
    difficulty: 'Intermediate',
    technicalGuidance: [
      'Use a tripod for sharp results',
      'Start with shutter speeds around 1/15 for subtle blur',
      'Consider using ND filters in bright conditions',
      'Enable mirror lock-up to reduce camera shake'
    ],
    creativeApproach: [
      'Combine static and moving elements',
      'Experiment with different shutter speeds',
      'Look for naturally moving subjects',
      'Try panning with moving subjects'
    ],
    inspiration: [
      {
        url: 'https://images.pexels.com/photos/1538177/pexels-photo-1538177.jpeg',
        description: 'Waterfall captured with long exposure to create smooth, silky water effect'
      },
      {
        url: 'https://images.pexels.com/photos/315938/pexels-photo-315938.jpeg',
        description: 'City traffic trails at night using long exposure'
      }
    ],
    featuredIn: [
      {
        title: 'Long Exposure Photography by Michael Freeman',
        source: 'https://www.amazon.com/Photographers-Eye-Composition-Design-Digital/dp/0240809343'
      }
    ]
  },
  {
    id: '4',
    title: 'Forced Perspective',
    description: 'Create an optical illusion by manipulating the viewer\'s perception of size and distance. This creative technique can produce surprising and engaging images that challenge reality.',
    category: 'Creative',
    difficulty: 'Intermediate',
    technicalGuidance: [
      'Use a small aperture for maximum depth of field',
      'Position subjects carefully to maintain illusion',
      'Consider using wide-angle lenses',
      'Pay attention to lighting consistency'
    ],
    creativeApproach: [
      'Plan your shot carefully before executing',
      'Use everyday objects in unexpected ways',
      'Think about scale and proportion',
      'Experiment with different viewing angles'
    ],
    inspiration: [
      {
        url: 'https://images.pexels.com/photos/1252873/pexels-photo-1252873.jpeg',
        description: 'Creative forced perspective shot making a person appear to hold a building'
      }
    ],
    featuredIn: [
      {
        title: 'Creative Photography Techniques',
        source: 'https://www.youtube.com/watch?v=example'
      }
    ]
  },
  {
    id: '5',
    title: 'Leading Lines',
    description: 'Find natural lines in your environment that draw the viewer\'s eye toward the main subject. This composition technique adds depth and direction to your images.',
    category: 'Composition',
    difficulty: 'Beginner',
    technicalGuidance: [
      'Look for natural lines in architecture and nature',
      'Consider the direction and flow of the lines',
      'Use different angles to strengthen the effect',
      'Pay attention to where lines intersect'
    ],
    creativeApproach: [
      'Combine multiple leading lines',
      'Use curves instead of straight lines',
      'Create tension with opposing lines',
      'Experiment with diagonal compositions'
    ],
    inspiration: [
      {
        url: 'https://images.pexels.com/photos/1722183/pexels-photo-1722183.jpeg',
        description: 'Railway tracks creating strong leading lines toward the horizon'
      },
      {
        url: 'https://images.pexels.com/photos/1738434/pexels-photo-1738434.jpeg',
        description: 'Urban architecture with strong diagonal lines leading to a focal point'
      }
    ],
    featuredIn: [
      {
        title: 'The Art of Composition',
        source: 'https://www.amazon.com/Photography-Composition-Principles-Images-Impact/dp/1681986744'
      }
    ]
  },
  {
    id: '6',
    title: 'High-Key Lighting',
    description: 'Create an image with predominantly bright tones and minimal shadows. This technique creates a clean, modern look perfect for portrait and product photography.',
    category: 'Lighting',
    difficulty: 'Intermediate',
    technicalGuidance: [
      'Use multiple light sources for even illumination',
      'Monitor histogram to prevent overexposure',
      'Consider using reflectors for fill light',
      'Choose appropriate background color'
    ],
    creativeApproach: [
      'Experiment with different background colors',
      'Try minimal compositions',
      'Look for naturally bright scenes',
      'Use negative space effectively'
    ],
    inspiration: [
      {
        url: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg',
        description: 'High-key portrait with minimal shadows and clean white background'
      }
    ],
    featuredIn: [
      {
        title: 'Studio Lighting Techniques',
        source: 'https://www.youtube.com/watch?v=example2'
      }
    ]
  },
  {
    id: '7',
    title: 'Focus Stacking',
    description: 'Take multiple images at different focus points and combine them for maximum depth of field. This advanced technique is perfect for macro and landscape photography.',
    category: 'Technical',
    difficulty: 'Advanced',
    technicalGuidance: [
      'Use manual focus for precise control',
      'Take multiple shots with different focus points',
      'Keep camera position absolutely stable',
      'Use appropriate software for combining images'
    ],
    creativeApproach: [
      'Choose subjects with interesting depth',
      'Plan your focus points carefully',
      'Consider artistic selective focus',
      'Experiment with different apertures'
    ],
    inspiration: [
      {
        url: 'https://images.pexels.com/photos/1918290/pexels-photo-1918290.jpeg',
        description: 'Macro photograph with complete front-to-back sharpness achieved through focus stacking'
      }
    ],
    featuredIn: [
      {
        title: 'Advanced Macro Photography',
        source: 'https://www.amazon.com/Understanding-Close-Up-Photography-Creative-Encounters/dp/0817427198'
      }
    ]
  },
  {
    id: '8',
    title: 'Double Exposure',
    description: 'Create an image by superimposing two different exposures to create a single image. This creative technique can produce dreamlike and surreal results.',
    category: 'Creative',
    difficulty: 'Advanced',
    technicalGuidance: [
      'Plan your exposures carefully',
      'Consider contrast between subjects',
      'Use manual exposure settings',
      'Pay attention to overlap areas'
    ],
    creativeApproach: [
      'Combine contrasting subjects',
      'Use silhouettes for one exposure',
      'Experiment with different blend modes',
      'Think about visual storytelling'
    ],
    inspiration: [
      {
        url: 'https://images.pexels.com/photos/1366957/pexels-photo-1366957.jpeg',
        description: 'Double exposure combining portrait and nature elements'
      }
    ],
    featuredIn: [
      {
        title: 'Creative Photography Masterclass',
        source: 'https://www.youtube.com/watch?v=example3'
      }
    ]
  },
  {
    id: '9',
    title: 'Negative Space',
    description: 'Compose an image where the subject is surrounded by empty space to create emphasis. This minimalist approach can create powerful, striking images.',
    category: 'Composition',
    difficulty: 'Intermediate',
    technicalGuidance: [
      'Look for clean, uncluttered backgrounds',
      'Consider subject placement carefully',
      'Use appropriate depth of field',
      'Pay attention to background texture'
    ],
    creativeApproach: [
      'Use color contrast effectively',
      'Experiment with different ratios of space',
      'Try both light and dark negative space',
      'Look for naturally minimal scenes'
    ],
    inspiration: [
      {
        url: 'https://images.pexels.com/photos/1939485/pexels-photo-1939485.jpeg',
        description: 'Minimalist composition using negative space to emphasize a single subject'
      }
    ],
    featuredIn: [
      {
        title: 'Minimalist Photography',
        source: 'https://www.amazon.com/Minimalist-Photography-Learning-See-Creatively/dp/1681986744'
      }
    ]
  },
  {
    id: '10',
    title: 'Backlighting',
    description: 'Position your subject between the camera and the light source to create rim lighting or silhouettes. This lighting technique can create dramatic and ethereal effects.',
    category: 'Lighting',
    difficulty: 'Intermediate',
    technicalGuidance: [
      'Use spot metering for accurate exposure',
      'Consider using fill flash',
      'Watch for lens flare',
      'Experiment with different angles'
    ],
    creativeApproach: [
      'Look for interesting silhouettes',
      'Use atmospheric conditions',
      'Try partial silhouettes',
      'Experiment with different times of day'
    ],
    inspiration: [
      {
        url: 'https://images.pexels.com/photos/1624496/pexels-photo-1624496.jpeg',
        description: 'Portrait using backlighting to create a dramatic silhouette'
      },
      {
        url: 'https://images.pexels.com/photos/1770809/pexels-photo-1770809.jpeg',
        description: 'Nature photography utilizing backlighting for rim light effect'
      }
    ],
    featuredIn: [
      {
        title: 'Natural Light Photography',
        source: 'https://www.amazon.com/Direction-Quality-Light-Photography-Illustrators/dp/1608956814'
      }
    ]
  }
];